import React, { useCallback } from "react";
import stopTypes from "../../../enums/stopTypes";
import { isRouteStatusInBuild } from "../../../models/routeDetail";
import {
  formatSecondsToHHMM,
  formatStopTime,
  formatTimeStringWithDefaultMaxValue,
  reformatTime,
} from "../../../utils/date";
import {
  formatToKg,
  getExceptionEntry,
  getRagStatusColor,
  countTotalByField,
  isPendingStop,
  getStopRequestType,
  getRagStatusTextColor,
  getStatusTextColor,
} from "../../../features/routeDetail/model";
import {
  ADDRESS,
  CAD,
  CHUTE,
  ETA,
  EXCEPTION,
  LOADED,
  OT,
  PARCELS,
  POSTCODE,
  PRIORITY,
  RAG,
  REQUEST,
  SMALLS,
  STOP,
  STOP_TIME,
  TYPE,
  WEIGHT,
  WINDOW,
  RETURN_TO_DEPOT,
  NON_COMPATIBLES_SHORT,
} from "../../../constants/strings";
import { COLUMN_ACCESSORS as CA } from "../../../constants/table";
import BodyCell from "../../common/Table/BodyCell";
import HeaderCell from "../../common/Table/HeaderCell";
import {
  Add,
  Consolidated,
  Remove,
  SpotIcon,
  Defer,
  MixedAddRemove,
} from "../../icons";
import { HighValueItem, ServiceItem } from "../../common/Table/Items";
import EditableCell from "../../common/Table/EditableCell";
import { requestIconType } from "../../../enums/requestTypes";

const stopListColumns = ({
  route,
  modifiers,
  columnsConfigs,
  headerCellClasses,
  bodyCellClasses,
}) => [
  {
    Cell: ({ rowData }) => (
      <HighValueItem rowData={rowData} classes={bodyCellClasses} />
    ),
    columnConfig: columnsConfigs[CA.HIGH_VALUE],
    accessor: CA.HIGH_VALUE,
  },
  {
    Cell: ({ value }) => (
      <BodyCell
        value={<ServiceItem value={value} />}
        classes={bodyCellClasses}
        extraCellClasses={[bodyCellClasses.cellTextBold]}
        testId={`${CA.SERVICE}-icon-${getStatusTextColor(value)}`}
      />
    ),
    columnConfig: columnsConfigs[CA.SERVICE],
    accessor: CA.SERVICE,
  },
  {
    Header: ({ data }) => {
      const subheader =
        columnsConfigs[CA.DISPLAY_NUMBER]?.isSubheader && data.length;
      return (
        <HeaderCell
          header={STOP}
          subheader={subheader}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value, rowData }) => {
      const data = rowData.type === stopTypes.break ? "" : value;
      return <BodyCell value={data} classes={bodyCellClasses} />;
    },
    columnConfig: columnsConfigs[CA.DISPLAY_NUMBER],
    accessor: CA.DISPLAY_NUMBER,
    sortByAccessor: CA.SEQUENCE_NUMBER,
  },
  {
    Header: () => {
      return <HeaderCell header={PRIORITY} classes={headerCellClasses} />;
    },
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.PRIORITY],
    accessor: CA.PRIORITY,
  },
  {
    Header: () => <HeaderCell header={TYPE} classes={headerCellClasses} />,
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.TYPE],
    accessor: CA.TYPE,
  },
  {
    Header: () => {
      return <HeaderCell header={ADDRESS} classes={headerCellClasses} />;
    },
    Cell: ({ value }) => (
      <BodyCell
        value={value}
        classes={bodyCellClasses}
        extraCellClasses={[bodyCellClasses.cellCroppedText]}
      />
    ),
    Footer: () => (
      <BodyCell
        value={RETURN_TO_DEPOT.toUpperCase()}
        classes={bodyCellClasses}
        testId={"tr-footer"}
      />
    ),
    columnConfig: columnsConfigs[CA.ADDRESS],
    accessor: CA.ADDRESS,
  },
  {
    Header: () => {
      return <HeaderCell header={POSTCODE} classes={headerCellClasses} />;
    },
    Cell: ({ value = "", rowData }) => {
      const isBreakStop = rowData.type === stopTypes.break;
      const isPending = isPendingStop(rowData);
      const isEditable = !(
        !modifiers ||
        isPending ||
        isRouteStatusInBuild(route)
      );
      const commonProps = {
        value,
        classes: bodyCellClasses,
      };
      return !isEditable || isBreakStop ? (
        <BodyCell {...commonProps} />
      ) : (
        <EditableCell
          {...commonProps}
          rowData={rowData}
          onSubmit={modifiers?.handleStopPostcodeChange}
        />
      );
    },
    columnConfig: columnsConfigs[CA.POSTCODE],
    accessor: CA.POSTCODE,
  },
  {
    Header: () => <HeaderCell header={RAG} classes={headerCellClasses} />,
    Cell: ({ value }) => {
      const icon = !!value && <SpotIcon color={getRagStatusColor(value)} />;
      return (
        <BodyCell
          value={icon}
          classes={bodyCellClasses}
          testId={`${CA.RAG}-icon-${getRagStatusTextColor(value)}`}
        />
      );
    },
    columnConfig: columnsConfigs[CA.RAG],
    accessor: CA.RAG,
  },
  {
    Header: ({ data }) => {
      const totalParcels =
        columnsConfigs[CA.PARCELS]?.isSubheader &&
        countTotalByField(data, CA.PARCELS);
      return (
        <HeaderCell
          subheader={totalParcels}
          header={PARCELS}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value, rowData }) => {
      const icon = rowData.consolidated && <Consolidated />;
      return (
        <BodyCell
          value={value}
          lastIcon={icon}
          classes={bodyCellClasses}
          extraContainerClasses={[bodyCellClasses.parcelContainer]}
          extraLastIconClasses={[bodyCellClasses.lastIconConsolidatedClass]}
          lastIconTestId={`${CA.PARCELS}-icon-consolidated`}
        />
      );
    },
    columnConfig: columnsConfigs[CA.PARCELS],
    accessor: CA.PARCELS,
  },
  {
    Header: ({ data }) => {
      const totalCadParcels =
        columnsConfigs[CA.CAD]?.isSubheader && countTotalByField(data, CA.CAD);

      return (
        <HeaderCell
          subheader={totalCadParcels}
          header={CAD}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.CAD],
    accessor: CA.CAD,
  },
  {
    Header: ({ data }) => {
      const totalSmalls =
        columnsConfigs[CA.SMALLS]?.isSubheader &&
        countTotalByField(data, CA.SMALLS);
      return (
        <HeaderCell
          subheader={totalSmalls}
          header={SMALLS}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.SMALLS],
    accessor: CA.SMALLS,
  },
  {
    Header: ({ data }) => {
      const totalLoaded = countTotalByField(data, CA.LOADED);
      const subheader = columnsConfigs[CA.LOADED]?.isSubheader && totalLoaded;
      return (
        <HeaderCell
          subheader={subheader}
          header={LOADED}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.LOADED],
    accessor: CA.LOADED,
  },
  {
    Header: () => <HeaderCell header={STOP_TIME} classes={headerCellClasses} />,
    Cell: ({ value, rowData }) => {
      const isPending = isPendingStop(rowData);
      const isEditable = !(
        !modifiers ||
        isPending ||
        isRouteStatusInBuild(route)
      );
      const time = formatStopTime(value || 0);
      const commonProps = {
        value: time,
        classes: bodyCellClasses,
      };
      return !isEditable ? (
        <BodyCell {...commonProps} />
      ) : (
        <EditableCell
          {...commonProps}
          rowData={rowData}
          onSubmit={modifiers?.handleStopTimeChange}
        />
      );
    },
    columnConfig: columnsConfigs[CA.STOP_TIME],
    accessor: CA.STOP_TIME,
  },
  {
    Header: () => <HeaderCell header={ETA} classes={headerCellClasses} />,
    Cell: ({ value }) => (
      <BodyCell value={reformatTime(value, true)} classes={bodyCellClasses} />
    ),
    Footer: () => (
      <BodyCell
        value={formatTimeStringWithDefaultMaxValue(route.routeEndTime)}
        classes={bodyCellClasses}
      />
    ),
    columnConfig: columnsConfigs[CA.ETA],
    accessor: CA.ETA,
  },
  {
    Header: ({ data }) => {
      const totalOpportunityTime =
        columnsConfigs[CA.OT]?.isSubheader && countTotalByField(data, CA.OT);

      return (
        <HeaderCell
          subheader={
            totalOpportunityTime && formatSecondsToHHMM(totalOpportunityTime)
          }
          header={OT}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value }) => {
      const time = !!value && formatSecondsToHHMM(value);
      return <BodyCell value={time} classes={bodyCellClasses} />;
    },
    Footer: () => (
      <BodyCell
        value={
          !!route.opportunityTime && formatSecondsToHHMM(route.opportunityTime)
        }
        classes={bodyCellClasses}
      />
    ),
    columnConfig: columnsConfigs[CA.OT],
    accessor: CA.OT,
  },
  {
    Header: ({ data }) => {
      const totalExceptions =
        columnsConfigs[CA.EXCEPTION]?.isSubheader &&
        data.reduce((total, row) => {
          return row.exception ? total + 1 : total;
        }, 0);
      return (
        <HeaderCell
          subheader={totalExceptions}
          header={EXCEPTION}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value }) => (
      <BodyCell value={getExceptionEntry(value)} classes={bodyCellClasses} />
    ),
    columnConfig: columnsConfigs[CA.EXCEPTION],
    accessor: CA.EXCEPTION,
  },
  {
    Header: () => {
      return <HeaderCell header={WINDOW} classes={headerCellClasses} />;
    },
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.WINDOW],
    accessor: CA.WINDOW,
  },
  {
    Header: ({ data }) => {
      const totalNonCompatibles =
        columnsConfigs[CA.NC]?.isSubheader &&
        data.reduce((total, row) => {
          return row.nonCompatibles ? total + 1 : total;
        }, 0);
      return (
        <HeaderCell
          subheader={totalNonCompatibles}
          header={NON_COMPATIBLES_SHORT}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.NC],
    accessor: CA.NC,
  },
  {
    Header: ({ data }) => {
      const totalWeight =
        columnsConfigs[CA.WEIGHT]?.isSubheader &&
        countTotalByField(data, CA.WEIGHT);
      return (
        <HeaderCell
          subheader={formatToKg(totalWeight)}
          header={WEIGHT}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value }) => (
      <BodyCell
        value={!!value && formatToKg(value)}
        classes={bodyCellClasses}
      />
    ),
    columnConfig: columnsConfigs[CA.WEIGHT],
    accessor: CA.WEIGHT,
  },
  {
    Header: () => {
      return <HeaderCell header={CHUTE} classes={headerCellClasses} />;
    },
    Cell: ({ value }) => <BodyCell value={value} classes={bodyCellClasses} />,
    columnConfig: columnsConfigs[CA.CHUTE],
    accessor: CA.CHUTE,
  },
  {
    Header: ({ data }) => {
      const totalRequests =
        columnsConfigs[CA.REQUESTS]?.isSubheader &&
        data.reduce((total, row) => {
          return row.requests ? total + row.requests.length : total;
        }, 0);
      return (
        <HeaderCell
          subheader={totalRequests}
          header={REQUEST}
          classes={headerCellClasses}
        />
      );
    },
    Cell: ({ value, rowData }) => {
      const onClick = useCallback(
        (e) => {
          e.stopPropagation();
          modifiers.onSelectStopRequests(rowData);
        },
        [rowData]
      );

      if (!value?.length) {
        return "";
      }

      const { type, count } = getStopRequestType(value);
      const bodyProps = { onClick };

      if (type === requestIconType.MIXED) {
        bodyProps.firstIcon = <MixedAddRemove />;
        bodyProps.onClick = null;
      } else if (type === requestIconType.ADD) {
        bodyProps.firstIcon = <Add />;
      } else if (type === requestIconType.REMOVE) {
        bodyProps.firstIcon = <Remove />;
      } else if (type === requestIconType.DEFFER) {
        bodyProps.firstIcon = <Defer />;
      } else {
        return "";
      }

      return (
        <BodyCell
          value={count}
          classes={bodyCellClasses}
          {...bodyProps}
          firstIconTestId={`${CA.REQUESTS}-icon-${type}`}
        />
      );
    },
    columnConfig: columnsConfigs[CA.REQUESTS],
    accessor: CA.REQUESTS,
  },
];

export default stopListColumns;
